/* ─── TableOps — Shared Types ──────────────────────────────────────── */

export type TableShape =
  | 'square' | 'rect' | 'round' | 'lshape'
  | 'cashier' | 'kitchen' | 'cafeteria' | 'bar' | 'pastry' | 'takeaway_delivery' | 'wc' | 'door' | 'window'

export interface Point2D {
  x: number
  y: number
}

export interface TableItem {
  id: number
  scopeKey: string
  x: number
  y: number
  w: number
  h: number
  rotation: number
  shape: TableShape
  label: string
  seats: number
  color: string
}

export interface ViewState {
  offsetX: number
  offsetY: number
  zoom: number
}

export interface FramePoly {
  id: number
  scopeKey: string
  points: Point2D[]
  closed: boolean
  color: string
  label: string
}

export interface SavedMap {
  name: string
  savedAt?: string
  tables: TableItem[]
  frames: FramePoly[]
  view?: ViewState
}

export interface MapListEntry {
  filename: string
  name: string
  savedAt: string | null
  tables: number
  frames: number
}

/** A persisted merge record — two originals collapsed into one merged table */
export interface MergeRecord {
  mergedId: number
  originals: [TableItem, TableItem]
}

/* ─── Register Scope Family ────────────────────────────────────────── */

/** A physical register (till) configuration — member of the scope family */
export interface RegisterConfig {
  number: number               // 1, 2, 3…
  label: string                // auto: "Register 1"
  payment_method_ids: string[] // linked from global catalog (empty = all)
}

/** Default register configs — always at least register 1 */
export const DEFAULT_REGISTER_CONFIGS: RegisterConfig[] = [
  { number: 1, label: 'Register 1', payment_method_ids: [] },
]

/* ─── Register (Cashier Shift / Till) ──────────────────────────────── */

/** A register shift — must be open before any table can be opened.
 *  ID is auto-generated as yymmddnn (nn=00,01,02… per day). */
export interface RegisterShift {
  id: string               // auto: yymmddnn
  register_number: number  // which register (scope family member)
  opened_at: string        // ISO
  closed_at?: string       // ISO — set when the shift ends
  opening_float: number    // cash in drawer at open
  status: 'open' | 'closed'
  /** Snapshot of closed tables during this shift (populated on close for analytics) */
  closedTables?: TableCloseRecord[]
  /** Local optimistic snapshot for TA/Delivery while server history revalidates. */
  fulfillment_takeaway_count?: number
  fulfillment_delivery_count?: number
  fulfillment_takeaway_revenue?: number
  fulfillment_delivery_revenue?: number
  fulfillment_total_tips?: number
}

/* ─── Payment Methods ──────────────────────────────────────────────── */

/** Payment method category for grouping in UI */
export type PaymentCategory = 'cash' | 'card' | 'digital'

/** A single payment method definition (scope entity) */
export interface PaymentMethod {
  id: string
  label: string
  icon: string
  category: PaymentCategory
  /** Sub-label shown in UI (e.g. "Visa", "Mastercard") */
  network?: string
  enabled: boolean
}

/** Default payment methods — canonical restaurant POS set */
export const DEFAULT_PAYMENT_METHODS: PaymentMethod[] = [
  { id: 'cash',               label: 'Cash',                icon: '💵', category: 'cash',    enabled: true },
  { id: 'credit_visa',        label: 'Credit Card',         icon: '💳', category: 'card',    network: 'Visa',       enabled: true },
  { id: 'credit_master',      label: 'Credit Card',         icon: '💳', category: 'card',    network: 'Mastercard', enabled: true },
  { id: 'debit_visa',         label: 'Debit Card',          icon: '💳', category: 'card',    network: 'Visa',       enabled: true },
  { id: 'debit_master',       label: 'Debit Card',          icon: '💳', category: 'card',    network: 'Mastercard', enabled: true },
  { id: 'mercadopago_qr',     label: 'MercadoPago',         icon: '📱', category: 'digital', network: 'QR',         enabled: true },
  { id: 'mercadopago_transfer', label: 'MercadoPago',       icon: '📲', category: 'digital', network: 'Transfer',   enabled: true },
]

/* ─── Check Payment (close check outcome) ──────────────────────────── */

/** Recorded when a check is closed via payment */
export interface CheckPayment {
  checkIndex: number
  scopeId: string
  paymentMethodId: string
  amount: number
  tip: number
  paid_at: string   // ISO
}

/** Aggregated item summary for shift analytics (top-N ranking) */
export interface ItemSummary {
  item_key: string
  name: string
  quantity: number
  revenue: number
}

/** Per-table close summary (all checks paid) */
export interface TableCloseRecord {
  scopeId: string
  session_key: string
  payments: CheckPayment[]
  closed_at: string
  subtotal: number
  total_tips: number
  grand_total: number
  /** Aggregated items consumed at this table (for shift analytics) */
  items?: ItemSummary[]
}

export type ActiveTool = 'select' | 'pen'
export type PenMode = 'free' | 'rect' | 'line'
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

export interface DragState {
  type: 'move' | 'resize' | 'pan' | 'frame-edge' | 'frame-vertex' | 'pen-rect'
  id?: number
  edge?: ResizeEdge
  frameId?: number
  edgeIdx?: number
  vertexIdx?: number
  nx?: number
  ny?: number
  origPts?: Point2D[]
  startX: number
  startY: number
  origX: number
  origY: number
  origW?: number
  origH?: number
  moved?: boolean
  origPositions?: Map<number, Point2D>
}

export interface Snapshot {
  tables: TableItem[]
  frames: FramePoly[]
}
